import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editWitnessProction2 } from "services/witnessProtectionService";
import { WitnessProtection } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  protection?: WitnessProtection;
  onClose: (refetch: boolean) => void;
};

const Grievance: FC<Props> = ({ protection, onClose }) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<WitnessProtection>();
  useEffect(() => {
    if (protection) {
      setFormState(protection);
    }
  }, [protection]);

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.grievance_description)
      return `${t("witness_protection.modals.grievance.grievance_description")} ${t("required")}.`;
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const api = editWitnessProction2;
    api({
      ...formState,
      id: protection?.id,
      grievance_submitted: true,
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("witness_protection.modals.grievance.title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("witness_protection.modals.grievance.form_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-2 gap-x-4 mt-4">
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("witness_protection.modals.grievance.grievance_description")}
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="grievance_description"
                value={formState?.grievance_description}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t("witness_protection.modals.grievance.new_decision_summary")}
              </label>
              <textarea
                rows={4}
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="new_decision_summary"
                value={formState?.new_decision_summary}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col mt-4 w-full">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                {t(
                  "witness_protection.modals.grievance.security_organization_involved",
                )}
              </label>
              <input
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="security_organization_involved"
                value={formState?.security_organization_involved}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("witness_protection.modals.grievance.submit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Grievance;
